import React, { useState, useEffect } from 'react';
import ContributionCalls from './ContributionCalls';
import { mutualisteAPI, paymentAPI, api, formatCurrency } from '../../services/api';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  MagnifyingGlassIcon, 
  UserIcon, 
  DocumentTextIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  PrinterIcon,
  CheckCircleIcon,
  XCircleIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';
import EnterpriseBulkPaymentFlow from '../../components/Payment/EnterpriseBulkPaymentFlow';
import '../../styles/AdminPanel.css';

/**
 * COMPOSANT : Gestion Unifiée des Paiements
 * 
 * Permet de créer et gérer tous types de paiements :
 * - RECETTES : Paiements vers la mutuelle (cotisations, épargnes, prêts)
 * - DÉPENSES : Paiements vers les mutualistes (prêts, remboursements, rachats)
 */
const PaymentUnified = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // États pour les données de formulaire
  const [categories, setCategories] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [filteredReasons, setFilteredReasons] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [selectedContributions, setSelectedContributions] = useState([]);

  // États pour la recherche de mutualiste
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMutualiste, setSelectedMutualiste] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // État du formulaire
  const [formData, setFormData] = useState({
    categoryCode: '',
    paymentReasonId: '',
    mutualisteId: '',
    amount: '',
    paymentMethod: 'ESPECES',
    mobileMoneyOperator: '',
    paymentType: 'THIRD_PARTY',
    policyId: '',
    externalRef: '',
    notes: ''
  });
  
  // État pour le montant calculé automatiquement
  const [isAmountAutoCalculated, setIsAmountAutoCalculated] = useState(false);

  // État modal
  const [showModal, setShowModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [showContributionCallsModal, setShowContributionCallsModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);

  // État pour la liste des paiements
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [gotoPage, setGotoPage] = useState('');
  const pageSize = 10;
  const [paymentPagination, setPaymentPagination] = useState({
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 1
  });

  // États pour la validation/annulation des dépenses PENDING
  const [cancelModal, setCancelModal] = useState({ open: false, payment: null, reason: '' });
  const [processingPaymentId, setProcessingPaymentId] = useState(null);

  // États pour les statistiques
  const [statistics, setStatistics] = useState({
    recettes: 0,
    depenses: 0
  });

  // États pour les filtres
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    method: '',
    operator: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  // Méthodes de paiement selon le type
  const paymentMethods = {
    individual: [
      { value: 'MOBILE_MONEY', label: 'Mobile Money' },
      { value: 'CARTE_BANCAIRE', label: 'Carte bancaire' },
      { value: 'ESPECES', label: 'Espèces' }
    ],
    enterprise: [
      { value: 'VIREMENT', label: 'Virement bancaire' },
      { value: 'CHEQUE', label: 'Chèque' }
    ]
  };

  // Opérateurs mobile money
  const mobileMoneyOperators = [
    { value: 'ORANGE', label: 'Orange Money' },
    { value: 'MTN', label: 'MTN Mobile Money' },
    { value: 'MOOV', label: 'Moov Money' },
    { value: 'WAVE', label: 'Wave' }
  ];

  // Charger les données initiales
  useEffect(() => {
    loadCategories();
    loadReasons();
    loadPayments();
  }, []);

  // Recharger les paiements après création
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadPayments();
      loadPaymentStats();
    }
  }, [refreshTrigger]);

  // Recharger la page de paiements quand les filtres ou la page changent
  useEffect(() => {
    loadPayments();
  }, [filters, currentPage]);

  // Recalculer les statistiques globales quand les filtres changent
  useEffect(() => {
    loadPaymentStats();
  }, [filters]);

  const getPaginationItems = (current, total, maxVisible = 9) => {
    const items = [];

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) items.push(i);
      return items;
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, current - half);
    let end = start + maxVisible - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) items.push(i);

    return items;
  };

  // Recherche de mutualistes avec délai
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchMutualistes(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Filtrer les raisons quand la catégorie change
  useEffect(() => {
    if (formData.categoryCode) {
      const filtered = reasons.filter(
        r => r.category.code === formData.categoryCode
      );
      setFilteredReasons(filtered);
      setFormData(prev => ({ ...prev, paymentReasonId: '' }));
    } else {
      setFilteredReasons([]);
    }
  }, [formData.categoryCode, reasons]);

  // Charger les polices du mutualiste sélectionné
  useEffect(() => {
    if (selectedMutualiste) {
      loadMutualistePolicies(selectedMutualiste.id);
      setFormData(prev => ({ ...prev, mutualisteId: selectedMutualiste.id }));
    } else {
      setPolicies([]);
      setFormData(prev => ({ ...prev, policyId: '', mutualisteId: '' }));
    }
  }, [selectedMutualiste]);

  // Mettre à jour la raison sélectionnée et filtrer les polices
  useEffect(() => {
    if (formData.paymentReasonId) {
      const reason = reasons.find(r => r.id === formData.paymentReasonId);
      setSelectedReason(reason);
      
      // Filtrer les polices selon le motif sélectionné
      if (reason) {
        const requiredType = getPolicyTypeForReason(reason.code);
        if (requiredType) {
          const filtered = policies.filter(p => p.offerType === requiredType);
          setFilteredPolicies(filtered);
          
          // Réinitialiser la police sélectionnée si elle n'est pas du bon type
          if (formData.policyId) {
            const currentPolicy = policies.find(p => p.id === formData.policyId);
            if (!currentPolicy || currentPolicy.offerType !== requiredType) {
              setFormData(prev => ({ ...prev, policyId: '' }));
            }
          }
        } else {
          setFilteredPolicies(policies);
        }
      }
    } else {
      setSelectedReason(null);
      setFilteredPolicies([]);
      setFormData(prev => ({ ...prev, policyId: '' }));
    }
  }, [formData.paymentReasonId, reasons, policies]);

  // Fonction pour déterminer le type de police requis selon le motif
  const getPolicyTypeForReason = (reasonCode) => {
    const mapping = {
      // Recettes
      'COTISATION': 'HEALTH_COVERAGE',
      'MENSUALITE_EPARGNE': 'SAVINGS',
      'ECHEANCE_PRET': 'LOAN',
      // Dépenses
      'PRET': 'LOAN',
      'REMBOURSEMENT_COTISATION': 'HEALTH_COVERAGE',
      'REMBOURSEMENT_FRAIS_MEDICAUX': 'HEALTH_COVERAGE',
      'RACHAT_EPARGNE': 'SAVINGS'
    };
    return mapping[reasonCode] || null;
  };

  const loadCategories = async () => {
    try {
      const response = await paymentAPI.getCategories();
      setCategories(response.data.data);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
      setError('Erreur lors du chargement des catégories');
    }
  };

  const loadPaymentStats = async () => {
    try {
      const params = {};

      if (filters.search) params.mutualisteSearch = filters.search;
      if (filters.method) params.paymentMethod = filters.method;
      if (filters.operator) params.operator = filters.operator;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.type) params.type = filters.type; // RECETTE | DEPENSE

      const response = await paymentAPI.getStats(params);

      const data = response.data?.data || response.data || {};

      setStatistics({
        recettes: data.recettes || 0,
        depenses: data.depenses || 0
      });
    } catch (error) {
      console.error('Erreur chargement statistiques paiements:', error);
      console.error('Détails:', error.response?.data);
    }
  };

  const loadPayments = async () => {
    try {
      setLoadingPayments(true);

      const params = {
        page: currentPage,
        limit: pageSize
      };

      if (filters.search) params.mutualisteSearch = filters.search;
      if (filters.type) params.type = filters.type; // Passer le filtre type (RECETTE/DEPENSE) à l'API
      if (filters.method) params.paymentMethod = filters.method;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      console.log('🔍 Filtres envoyés à l\'API:', params);

      const response = await paymentAPI.getPaymentHistory(params);

      let paymentsData = response.data?.data || response.data?.payments || [];
      const pagination = response.data?.pagination || {};

      console.log('📊 Paiements reçus de l\'API:', paymentsData.length);
      if (paymentsData.length > 0) {
        console.log('Premier paiement:', {
          receiptNumber: paymentsData[0].receiptNumber,
          amount: paymentsData[0].amount,
          reason: paymentsData[0].payment_reasons?.code,
          category: paymentsData[0].payment_reasons?.category?.code
        });
      }

      // Filtrer par opérateur mobile money côté frontend (si MOBILE_MONEY sélectionné)
      if (filters.operator) {
        paymentsData = paymentsData.filter(payment =>
          payment.paymentMethod === 'MOBILE_MONEY' &&
          payment.mobileMoneyOperator === filters.operator
        );
      }

      setPayments(paymentsData);

      setPaymentPagination(prev => ({
        page: pagination.page || currentPage,
        limit: pagination.limit || pageSize,
        total: pagination.total || prev.total,
        totalPages: pagination.pages || pagination.totalPages || prev.totalPages || 1
      }));
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      console.error('Détails:', error.response?.data);
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadReasons = async () => {
    try {
      const response = await paymentAPI.getReasons();
      setReasons(response.data.data);
    } catch (err) {
      console.error('Erreur chargement raisons:', err);
      setError('Erreur lors du chargement des raisons');
    }
  };

  // Charger les contributions impayées d'une police
  const loadUnpaidContributions = async (policyId) => {
    try {
      const response = await api.get(`/contributions/policy/${policyId}/unpaid`);
      const sorted = (response.data.data || []).sort((a, b) => 
        new Date(a.dueDate) - new Date(b.dueDate)
      );
      setContributions(sorted);
    } catch (err) {
      console.error('Erreur chargement contributions:', err);
      setContributions([]);
    }
  };

  // Charger les contributions payées d'une police (pour remboursements)
  const loadPaidContributions = async (policyId) => {
    try {
      const response = await api.get(`/contributions/policy/${policyId}/paid`);
      const sorted = (response.data.data || []).sort((a, b) => 
        new Date(a.dueDate) - new Date(b.dueDate)
      );
      setContributions(sorted);
    } catch (err) {
      console.error('Erreur chargement contributions payées:', err);
      setContributions([]);
    }
  };

  // Toggle sélection contribution
  const toggleContribution = (contributionId) => {
    setSelectedContributions(prev => {
      if (prev.includes(contributionId)) {
        return prev.filter(id => id !== contributionId);
      } else {
        return [...prev, contributionId];
      }
    });
  };

  // Calcul du montant total des contributions sélectionnées
  const calculateTotalAmount = () => {
    return contributions
      .filter(c => selectedContributions.includes(c.id))
      .reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  };

  // Mettre à jour le montant automatiquement quand les contributions changent
  useEffect(() => {
    if (isAmountAutoCalculated && selectedContributions.length > 0) {
      const total = calculateTotalAmount();
      setFormData(prev => ({ ...prev, amount: total }));
    }
  }, [selectedContributions, contributions, isAmountAutoCalculated]);

  // Déterminer si un motif nécessite la sélection de contributions
  const reasonRequiresContributions = (reasonCode) => {
    return ['COTISATION', 'MENSUALITE_EPARGNE', 'ECHEANCE_PRET', 'REMBOURSEMENT_COTISATION'].includes(reasonCode);
  };

  // Déterminer si on doit charger des contributions payées (pour remboursement) ou impayées
  const reasonRequiresPaidContributions = (reasonCode) => {
    return reasonCode === 'REMBOURSEMENT_COTISATION';
  };

  const searchMutualistes = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await mutualisteAPI.searchMutualistes({
        search: query,
        limit: 10
      });
      setSearchResults(response.data?.data?.mutualistes || response.data?.mutualistes || []);
    } catch (err) {
      console.error('Erreur recherche mutualistes:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const loadMutualistePolicies = async (mutualisteId) => {
    try {
      // Charger le mutualiste avec ses souscriptions et polices
      const response = await mutualisteAPI.getMutualiste(mutualisteId);
      const mutualiste = response.data;
      
      // Extraire les polices depuis les souscriptions
      const allPolicies = [];
      if (mutualiste.subscriptions) {
        for (const subscription of mutualiste.subscriptions) {
          if (subscription.policy && ['ACTIVE', 'PENDING'].includes(subscription.policy.status)) {
            allPolicies.push({
              ...subscription.policy,
              offerName: subscription.offer?.name,
              offerType: subscription.offer?.type
            });
          }
        }
      }
      
      setPolicies(allPolicies);
    } catch (err) {
      console.error('Erreur chargement polices:', err);
      setPolicies([]);
    }
  };

  // Formater le montant avec séparation des milliers
  const formatAmount = (value) => {
    if (!value) return '';
    // Retirer tous les espaces et convertir en nombre
    const numValue = parseFloat(value.toString().replace(/\s/g, ''));
    if (isNaN(numValue)) return '';
    // Formater avec séparation des milliers
    return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  // Formater la méthode de paiement
  const formatPaymentMethod = (payment) => {
    if (payment.paymentMethod === 'MOBILE_MONEY' && payment.mobileMoneyOperator) {
      const operatorLabels = {
        'ORANGE': 'Orange Money',
        'MTN': 'MTN Money',
        'MOOV': 'Moov Money',
        'WAVE': 'Wave'
      };
      return `Mobile Money (${operatorLabels[payment.mobileMoneyOperator] || payment.mobileMoneyOperator})`;
    }
    
    const methodLabels = {
      'ESPECES': 'Espèces',
      'VIREMENT': 'Virement',
      'CHEQUE': 'Chèque',
      'CARTE_BANCAIRE': 'Carte bancaire',
      'MOBILE_MONEY': 'Mobile Money'
    };
    return methodLabels[payment.paymentMethod] || payment.paymentMethod;
  };

  // Fonction pour gérer les changements de filtres
  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Si on change la méthode et que ce n'est plus MOBILE_MONEY, réinitialiser l'opérateur
      if (field === 'method' && value !== 'MOBILE_MONEY') {
        newFilters.operator = '';
      }
      
      return newFilters;
    });
    setCurrentPage(1);
    setGotoPage('');
  };
  const totalPages = paymentPagination.totalPages || 1;

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si on change le type de paiement (catégorie), réinitialiser les champs dépendants
    if (name === 'categoryCode') {
      setFormData(prev => ({
        ...prev,
        categoryCode: value,
        paymentReasonId: '',
        policyId: '',
        amount: ''
      }));
      setContributions([]);
      setSelectedContributions([]);
      setIsAmountAutoCalculated(false);
      return;
    }
    
    // Si on change le motif de paiement, réinitialiser les champs dépendants
    if (name === 'paymentReasonId') {
      setFormData(prev => ({
        ...prev,
        paymentReasonId: value,
        policyId: '',
        amount: ''
      }));
      setContributions([]);
      setSelectedContributions([]);
      setIsAmountAutoCalculated(false);
      return;
    }

    // Si on change la méthode de paiement, réinitialiser l'opérateur
    if (name === 'paymentMethod') {
      setFormData(prev => ({
        ...prev,
        paymentMethod: value,
        mobileMoneyOperator: '' // Reset operator
      }));
      return;
    }
    
    // Si on change la police, charger les contributions si nécessaire
    if (name === 'policyId' && value && selectedReason) {
      if (reasonRequiresContributions(selectedReason.code)) {
        // Charger contributions payées pour remboursement, impayées pour paiement
        if (reasonRequiresPaidContributions(selectedReason.code)) {
          loadPaidContributions(value);
        } else {
          loadUnpaidContributions(value);
        }
        setIsAmountAutoCalculated(true);
      } else {
        setContributions([]);
        setSelectedContributions([]);
        setIsAmountAutoCalculated(false);
      }
    }
    
    // Gérer le champ amount : retirer les espaces pour stocker le nombre
    if (name === 'amount') {
      const numericValue = value.replace(/\s/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validation
      if (!formData.categoryCode) {
        throw new Error('Veuillez sélectionner un type de paiement');
      }
      if (!formData.paymentReasonId) {
        throw new Error('Veuillez sélectionner un motif');
      }
      if (!selectedMutualiste || !formData.mutualisteId) {
        throw new Error('Veuillez sélectionner un mutualiste');
      }
      if (!formData.amount || formData.amount <= 0) {
        throw new Error('Veuillez saisir un montant valide');
      }

      // Vérifier si police requise selon le motif
      if (selectedReason) {
        const requiredType = getPolicyTypeForReason(selectedReason.code);
        if (requiredType && !formData.policyId) {
          throw new Error(`Une police de type ${getPolicyTypeLabel(requiredType)} est requise pour ce motif`);
        }
        // Vérifier qu'une police du bon type existe
        if (requiredType && filteredPolicies.length === 0) {
          throw new Error(`Le mutualiste n'a aucune police de type ${getPolicyTypeLabel(requiredType)} active`);
        }
        
        // Vérifier la sélection de contributions si nécessaire
        if (reasonRequiresContributions(selectedReason.code)) {
          if (selectedContributions.length === 0) {
            throw new Error('Veuillez sélectionner au moins une contribution à payer');
          }
        }
      }

      // Préparer les données
      const paymentData = {
        paymentReasonId: formData.paymentReasonId,
        mutualisteId: formData.mutualisteId,
        amount: isAmountAutoCalculated && selectedContributions.length > 0 
          ? calculateTotalAmount() 
          : parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        ...(formData.paymentMethod === 'MOBILE_MONEY' && {
          mobileMoneyOperator: formData.mobileMoneyOperator
        }),
        paymentType: formData.paymentType,
        policyId: formData.policyId || undefined,
        externalRef: formData.externalRef || undefined,
        notes: formData.notes || undefined,
        // Ajouter les IDs des contributions si présentes
        contributionIds: selectedContributions.length > 0 ? selectedContributions : undefined
      };

      console.log('Données envoyées au backend:', paymentData);

      // Créer le paiement
      const response = await paymentAPI.createUnifiedPayment(paymentData);
      
      // Afficher toast de succès
      toast.success(`Paiement créé avec succès ! N° ${response.data.data.receiptNumber}`);
      
      // Rafraîchir la liste des paiements
      setRefreshTrigger(prev => prev + 1);
      
      // Fermer immédiatement la modale
      handleCloseModal();

    } catch (err) {
      console.error('Erreur création paiement:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la création du paiement');
    } finally {
      setLoading(false);
    }
  };

  const getPolicyTypeLabel = (type) => {
    const labels = {
      'HEALTH_COVERAGE': 'Santé',
      'SAVINGS': 'Épargne',
      'LOAN': 'Prêt'
    };
    return labels[type] || type;
  };

  const resetForm = () => {
    setFormData({
      categoryCode: '',
      paymentReasonId: '',
      mutualisteId: '',
      amount: '',
      paymentMethod: 'ESPECES',
      mobileMoneyOperator: '',
      paymentType: 'THIRD_PARTY',
      policyId: '',
      externalRef: '',
      notes: ''
    });
    setSearchQuery('');
    setSelectedMutualiste(null);
    setSearchResults([]);
    setPolicies([]);
    setFilteredPolicies([]);
    setContributions([]);
    setSelectedContributions([]);
    setIsAmountAutoCalculated(false);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleCloseEnterpriseModal = () => {
    setShowEnterpriseModal(false);
    // Rafraîchir systématiquement la liste + les statistiques :
    // couvre à la fois la fin de traitement et la fermeture manuelle.
    setRefreshTrigger(prev => prev + 1);
  };

  const handleValidatePayment = async (payment) => {
    if (!window.confirm(`Confirmer la validation du paiement ${payment.receiptNumber} ?`)) return;
    setProcessingPaymentId(payment.id);
    try {
      const token = localStorage.getItem('accessToken');
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.put(`${baseUrl}/payments/${payment.id}/status`, { status: 'COMPLETED' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Paiement ${payment.receiptNumber} validé avec succès`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Erreur validation paiement:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la validation');
    } finally {
      setProcessingPaymentId(null);
    }
  };

  const handleCancelPayment = async () => {
    const { payment, reason } = cancelModal;
    if (!reason.trim()) {
      toast.error('Un motif d\'annulation est requis');
      return;
    }
    setProcessingPaymentId(payment.id);
    try {
      const token = localStorage.getItem('accessToken');
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.put(`${baseUrl}/payments/${payment.id}/status`, { status: 'CANCELLED', reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Paiement ${payment.receiptNumber} annulé`);
      setCancelModal({ open: false, payment: null, reason: '' });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Erreur annulation paiement:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'annulation');
    } finally {
      setProcessingPaymentId(null);
    }
  };

  const getCategoryBadgeClass = (code) => {
    return code === 'RECETTE' ? 'badge-success' : 'badge-warning';
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Paiements
            </h1>
            <p className="text-gray-600">
              Créer et gérer tous les paiements (recettes et dépenses)
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button 
              onClick={() => setShowModal(true)}
              className="btn-primary flex items-center justify-center whitespace-nowrap"
            >
              <BanknotesIcon className="h-5 w-5 mr-2" />
              Nouveau Paiement
            </button>
            <button 
              onClick={() => setShowEnterpriseModal(true)}
              className="btn-secondary flex items-center justify-center whitespace-nowrap"
            >
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Traitement de cotisations
            </button>
            <button 
              onClick={() => setShowContributionCallsModal(true)}
              className="flex items-center justify-center whitespace-nowrap px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-medium transition-colors"
            >
              <BellAlertIcon className="h-5 w-5 mr-2" />
              Appel de cotisation
            </button>
          </div>
        </div>
      </div>

      {/* Modal de création */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header avec gradient */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white p-2 rounded-lg">
                    <BanknotesIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Nouveau Paiement</h3>
                    <p className="text-sm text-primary-100">Recette ou dépense unifiée</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-primary-100 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white">
              <div className="px-6 py-6 max-h-[calc(90vh-200px)] overflow-y-auto">
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    {success}
                  </div>
                )}

                <div className="space-y-6">

                  {/* Sélection du mutualiste avec recherche - EN PREMIER */}
                  {!selectedMutualiste ? (
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                        Rechercher un mutualiste
                      </h4>

                      <div className="relative">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Rechercher par matricule ou nom..."
                          />
                        </div>

                        {/* Résultats de recherche - Dropdown absolu */}
                        {searchResults.length > 0 && (
                          <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                            {searchResults.map((mutualiste) => (
                              <button
                                key={mutualiste.id}
                                type="button"
                                onClick={() => {
                                  setSelectedMutualiste(mutualiste);
                                  setSearchQuery('');
                                  setSearchResults([]);
                                }}
                                className="w-full p-4 hover:bg-blue-50 transition-colors duration-200 text-left border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-semibold text-gray-900">
                                  {mutualiste.nom} {mutualiste.prenoms}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  Matricule: {mutualiste.matricule}
                                </div>
                                {mutualiste.enterprise && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {mutualiste.enterprise.nom}
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Messages d'état */}
                        {isSearching && searchQuery && (
                          <div className="text-center py-4 text-gray-500">
                            Recherche en cours...
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Mutualiste sélectionné */
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <UserIcon className="h-4 w-4 mr-2 text-blue-600" />
                        Mutualiste sélectionné
                      </h3>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {selectedMutualiste.nom} {selectedMutualiste.prenoms}
                            <span className="text-sm text-gray-600 font-normal ml-2">
                              • Matricule: {selectedMutualiste.matricule}
                            </span>
                          </div>
                          {selectedMutualiste.enterprise && (
                            <div className="text-xs text-gray-500 mt-1">
                              {selectedMutualiste.enterprise.nom}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedMutualiste(null)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Changer
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Type (Catégorie) et Motif (Raison) sur la même ligne */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Type de paiement - Boutons radio */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <BanknotesIcon className="h-5 w-5 text-blue-600" />
                        <label className="text-sm font-semibold text-gray-900">Type de paiement *</label>
                      </div>
                      <div className="space-y-2">
                        {categories.map((cat) => (
                          <label
                            key={cat.id}
                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              formData.categoryCode === cat.code
                                ? 'border-blue-500 bg-blue-100'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="categoryCode"
                              value={cat.code}
                              checked={formData.categoryCode === cat.code}
                              onChange={handleChange}
                              className="form-radio text-blue-600 h-4 w-4"
                              required
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{cat.libelle}</p>
                              <p className="text-xs text-gray-500">{cat.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Motif et Police - Même zone */}
                    <div className={`rounded-lg p-4 ${formData.categoryCode ? 'bg-green-50' : 'bg-gray-100'}`}>
                      {/* Motif */}
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Motif *
                        {!formData.categoryCode && (
                          <span className="text-xs font-normal text-gray-500 ml-2">(Sélectionnez d'abord un type)</span>
                        )}
                      </label>
                      <select
                        name="paymentReasonId"
                        value={formData.paymentReasonId}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        required
                        disabled={!formData.categoryCode}
                      >
                        <option value="">-- Sélectionner un motif --</option>
                        {filteredReasons.map(reason => (
                          <option key={reason.id} value={reason.id}>
                            {reason.libelle}
                            {reason.requiresPolicy && ' (Police requise)'}
                          </option>
                        ))}
                      </select>

                      {/* Police (affichée sous le motif) */}
                      {selectedReason && getPolicyTypeForReason(selectedReason.code) && (
                        <div className="mt-4">
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Police concernée *
                          </label>
                          {!selectedMutualiste ? (
                            <div className="p-3 bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-800">
                              ℹ️ Sélectionnez d'abord un mutualiste pour voir ses polices de type <strong>{getPolicyTypeLabel(getPolicyTypeForReason(selectedReason.code))}</strong>
                            </div>
                          ) : filteredPolicies.length > 0 ? (
                            <select
                              name="policyId"
                              value={formData.policyId}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              required
                            >
                              <option value="">-- Sélectionner une police --</option>
                              {filteredPolicies.map(pol => (
                                <option key={pol.id} value={pol.id}>
                                  {pol.policyNumber} - {pol.offerName}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="p-3 bg-orange-100 border border-orange-200 rounded-lg text-sm text-orange-800">
                              ⚠️ Aucune police de type <strong>{getPolicyTypeLabel(getPolicyTypeForReason(selectedReason.code))}</strong> active pour ce mutualiste
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Contributions impayées (pour COTISATION, MENSUALITE_EPARGNE, ECHEANCE_PRET) */}
                  {formData.policyId && selectedReason && reasonRequiresContributions(selectedReason.code) && contributions.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">
                        Contributions à payer *
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {contributions.map((contribution) => (
                          <label
                            key={contribution.id}
                            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors bg-white"
                          >
                            <div className="flex items-center flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={selectedContributions.includes(contribution.id)}
                                onChange={() => toggleContribution(contribution.id)}
                                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                              />
                              <div className="ml-3 flex items-center gap-2 min-w-0">
                                <span className="font-medium text-gray-900">
                                  {new Date(contribution.dueDate).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'long'
                                  })}
                                </span>
                                {contribution.status === 'OVERDUE' && (
                                  <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex-shrink-0">
                                    Retard
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="font-bold text-base text-blue-600 ml-4 flex-shrink-0">
                              {formatCurrency(contribution.amount)}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Méthode de paiement, Opérateur, Référence externe et Montant */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Méthode de paiement */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Méthode de paiement *</label>
                      <select
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      >
                        <optgroup label="Entreprises">
                          {paymentMethods.enterprise.map(method => (
                            <option key={method.value} value={method.value}>
                              {method.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Mutualistes">
                          {paymentMethods.individual.map(method => (
                            <option key={method.value} value={method.value}>
                              {method.label}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>

                    {/* Opérateur Mobile Money (conditionnel) */}
                    {formData.paymentMethod === 'MOBILE_MONEY' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Opérateur *</label>
                        <select
                          name="mobileMoneyOperator"
                          value={formData.mobileMoneyOperator}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="">Sélectionner l'opérateur...</option>
                          {mobileMoneyOperators.map(operator => (
                            <option key={operator.value} value={operator.value}>
                              {operator.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Référence externe */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Référence externe</label>
                      <input
                        type="text"
                        name="externalRef"
                        value={formData.externalRef}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Ex: TRX123456789"
                      />
                    </div>

                    {/* Montant */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Montant (F CFA) *
                        {isAmountAutoCalculated && selectedContributions.length > 0 && (
                          <span className="text-xs text-green-600 ml-2">
                            ({selectedContributions.length} sélectionnée{selectedContributions.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        name="amount"
                        value={formatAmount(isAmountAutoCalculated && selectedContributions.length > 0 ? calculateTotalAmount() : formData.amount)}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-right font-semibold"
                        placeholder="Ex: 50 000"
                        required
                        disabled={isAmountAutoCalculated && selectedContributions.length > 0}
                      />
                    </div>
                  </div>

                  {/* Notes - Masqué pour l'instant */}
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      rows="3"
                      placeholder="Informations complémentaires..."
                    />
                  </div> */}
                </div>
              </div>

              {/* Footer avec boutons */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {loading ? 'Création...' : 'Créer le Paiement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Appel de cotisation */}
      {showContributionCallsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowContributionCallsModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white p-2 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Appel de cotisation</h3>
                    <p className="text-sm text-primary-100">Génération et envoi des appels à cotisation</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowContributionCallsModal(false)}
                  className="text-primary-100 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="bg-white px-6 py-6 max-h-[calc(90vh-120px)] overflow-y-auto">
              <ContributionCalls
                embedded={true}
                onClose={() => setShowContributionCallsModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de traitement de cotisations entreprises */}
      {showEnterpriseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCloseEnterpriseModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header avec gradient */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white p-2 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Traitement de cotisations</h3>
                    <p className="text-sm text-primary-100">Paiements de masse pour les entreprises</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseEnterpriseModal}
                  className="text-primary-100 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="bg-white px-6 py-6 max-h-[calc(90vh-120px)] overflow-y-auto">
              <EnterpriseBulkPaymentFlow
                onCompleted={() => {
                  toast.success('Traitement des cotisations entreprises effectué avec succès');
                  handleCloseEnterpriseModal();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Recettes</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(statistics.recettes)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                <CurrencyDollarIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Dépenses</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(statistics.depenses)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-card p-6 space-y-4">
        {/* Ligne 1 : dates + recherche */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Date début</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Date fin</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Recherche</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="N° reçu, mutualiste..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Ligne 2 : type, méthode, opérateur */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Type</label>
            <select
              className="form-input"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">Tous les types</option>
              <option value="RECETTE">Recettes</option>
              <option value="DEPENSE">Dépenses</option>
            </select>
          </div>
          <div>
            <label className="form-label">Méthode</label>
            <select
              className="form-input"
              value={filters.method}
              onChange={(e) => handleFilterChange('method', e.target.value)}
            >
              <option value="">Toutes les méthodes</option>
              <option value="MOBILE_MONEY">Mobile Money</option>
              <option value="CARTE_BANCAIRE">Carte bancaire</option>
              <option value="ESPECES">Espèces</option>
              <option value="VIREMENT">Virement</option>
              <option value="CHEQUE">Chèque</option>
            </select>
          </div>
          <div>
            <label className="form-label">Statut</label>
            <select
              className="form-input"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="PENDING">En attente</option>
              <option value="PROCESSING">En cours</option>
              <option value="COMPLETED">Complété</option>
              <option value="FAILED">Échoué</option>
              <option value="CANCELLED">Annulé</option>
            </select>
          </div>
          {filters.method === 'MOBILE_MONEY' && (
            <div>
              <label className="form-label">Opérateur</label>
              <select
                className="form-input"
                value={filters.operator}
                onChange={(e) => handleFilterChange('operator', e.target.value)}
              >
                <option value="">Tous les opérateurs</option>
                <option value="ORANGE">Orange Money</option>
                <option value="MTN">MTN Money</option>
                <option value="MOOV">Moov Money</option>
                <option value="WAVE">Wave</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Liste des paiements */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motif
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payeur
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Méthode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  N° Reçu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingPayments ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center">
                    <p className="text-gray-500">Chargement...</p>
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <BanknotesIcon className="h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-gray-500 text-sm">
                        Aucun paiement enregistré pour le moment
                      </p>
                      <p className="text-gray-400 text-xs mt-1">
                        Cliquez sur "Nouveau Paiement" pour créer votre premier paiement
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    {/* Type */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        payment.payment_reasons?.category?.code === 'RECETTE' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {payment.payment_reasons?.category?.libelle || 'N/A'}
                      </span>
                    </td>
                    {/* Motif */}
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {payment.payment_reasons?.libelle || 'N/A'}
                      </span>
                    </td>
                    {/* Payeur */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payment.mutualiste?.nom} {payment.mutualiste?.prenoms}
                      </div>
                      <div className="text-xs text-gray-500">
                        {payment.mutualiste?.matricule}
                      </div>
                    </td>
                    {/* Montant */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(payment.amount)}
                      </span>
                    </td>
                    {/* Méthode */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {formatPaymentMethod(payment)}
                      </span>
                    </td>
                    {/* Date avec heure */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(payment.createdAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(payment.createdAt).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </div>
                    </td>
                    {/* N° Reçu */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {payment.receiptNumber}
                      </span>
                    </td>
                    {/* Statut */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        payment.status === 'PROCESSING' ? 'bg-blue-100 text-blue-800' :
                        payment.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                        payment.status === 'CANCELLED' ? 'bg-gray-100 text-gray-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {payment.status === 'COMPLETED' ? 'Complété' :
                         payment.status === 'PENDING' ? 'En attente' :
                         payment.status === 'PROCESSING' ? 'En traitement' :
                         payment.status === 'FAILED' ? 'Échoué' :
                         payment.status === 'CANCELLED' ? 'Annulé' :
                         payment.status}
                      </span>
                    </td>
                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <div className="flex items-center justify-center space-x-1">
                      {payment.status === 'PENDING' && payment.payment_reasons?.category?.code === 'DEPENSE' && (
                        <>
                          <button
                            onClick={() => handleValidatePayment(payment)}
                            disabled={processingPaymentId === payment.id}
                            className="text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
                            title="Valider le paiement"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setCancelModal({ open: true, payment, reason: '' })}
                            disabled={processingPaymentId === payment.id}
                            className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="Annuler le paiement"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {payment.status === 'COMPLETED' && (
                        <button
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('accessToken');
                              const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                              const categoryCode = payment.payment_reasons?.category?.code;
                              const isDepense = categoryCode === 'DEPENSE';

                              const url = isDepense
                                ? `${baseUrl}/payments/${payment.id}/document`
                                : `${baseUrl}/payments/${payment.id}/receipt-duplicate`;

                              const response = await axios.get(url, {
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob'
                              });
                              const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = fileUrl;
                              link.setAttribute('download', `recu-${payment.receiptNumber}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(fileUrl);
                              toast.success('Reçu téléchargé avec succès');
                            } catch (error) {
                              console.error('Erreur téléchargement reçu:', error);
                              toast.error('Erreur lors du téléchargement du reçu');
                            }
                          }}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Télécharger le reçu"
                        >
                          <PrinterIcon className="h-5 w-5" />
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {payments.length > 0 && totalPages > 1 && (
          <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> sur{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={gotoPage}
                    onChange={(e) => setGotoPage(e.target.value)}
                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Aller à"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const pageNumber = parseInt(gotoPage, 10);
                      if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
                        setCurrentPage(pageNumber);
                      }
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Aller
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
                >
                  Préc.
                </button>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {getPaginationItems(currentPage, totalPages).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </nav>
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
                >
                  Suiv.
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modale d'annulation d'une dépense PENDING */}
      {cancelModal.open && cancelModal.payment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <XCircleIcon className="h-6 w-6 text-white" />
                  <h2 className="text-lg font-semibold text-white">Annuler le paiement</h2>
                </div>
                <button
                  onClick={() => setCancelModal({ open: false, payment: null, reason: '' })}
                  className="text-white hover:text-red-200"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-700">
                Annuler le paiement <span className="font-semibold">{cancelModal.payment.receiptNumber}</span> de{' '}
                <span className="font-semibold">{formatCurrency(cancelModal.payment.amount)}</span> ?
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motif d'annulation <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Saisir le motif d'annulation..."
                  value={cancelModal.reason}
                  onChange={(e) => setCancelModal(prev => ({ ...prev, reason: e.target.value }))}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={() => setCancelModal({ open: false, payment: null, reason: '' })}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Retour
                </button>
                <button
                  onClick={handleCancelPayment}
                  disabled={processingPaymentId === cancelModal.payment.id}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {processingPaymentId === cancelModal.payment.id ? 'Annulation...' : 'Confirmer l\'annulation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentUnified;
